import request from '@/utils/request'
import { useAppStore, type Shop, type Section, type RevenueIndication } from '@/stores/app'
import shopsService from '@/services/shopsService'
import * as XLSX from 'xlsx'
import i18n from '@/utils/i18n'
import localeMessages from '@/utils/i18n/locales'
import textHelpers from '@/utils/textHelpers'

// Core types for revenue data
interface RevenueItem {
  ShoppingCenterName: string
  ShopName: string
  ChainShopId: string | null
  SectionId: number
  SectionName: string
  Month: number
  Year: number
  RevenueInteger: number | null
  IsShopDisabled: boolean
}

interface PreparedRevenueData {
  revenueValue: number | undefined
  shopId: number
  sectionId: number
  shoppingCenterGroupId: number
  chainShopId: string | null
}

interface ValidationResult {
  success: boolean
  errors: ValidationError[]
}

interface ValidationError {
  failureReason: string
  relatedFailureParameter?: string
  identifierDescription?: string
}

interface HeaderTranslation {
  key: string
  header: string
}

interface IndexedHeader {
  v: string
  adress: string
}

type SupportedLocale = keyof typeof localeMessages

const revenueFileHeaderDefinitions = [
  { key: 'ShopName', translationKey: 'ShopName' },
  { key: 'ShoppingCenterName', translationKey: 'ShoppingCenterName' },
  { key: 'SectionId', translationKey: 'SectionId' },
  { key: 'SectionName', translationKey: 'SectionName' },
  { key: 'ChainShopId', translationKey: 'ChainShopId' },
  { key: 'Month', translationKey: 'Month' },
  { key: 'IsShopDisabled', translationKey: 'ShopDisabled' },
  { key: 'Year', translationKey: 'Year' },
  { key: 'RevenueInteger', translationKey: 'RevenueInteger' },
] as const

type RevenueFileHeaderKey = (typeof revenueFileHeaderDefinitions)[number]['key']

interface PostRevenueResponse {
  updateSucceed: boolean
  shopId: number
  sectionId: number
}

interface RevenueIndicationOptions {
  applyToStore?: boolean
}

interface RawRevenueIndicationItem {
  shopId: number | string
  sectionIds: Array<number | string>
}

interface RawRevenueIndication {
  data: RawRevenueIndicationItem[]
  length: number
}

type RevenueIndicationResult =
  | {
      success: true
      data: RevenueIndication
    }
  | {
      success: false
      error: 'request_failed' | 'unexpected_error'
      details?: string
    }

// Translation helper type
interface TranslationFunction {
  (key: string): string
}

const numberWithSeparatorsRegEx = '^\\d+(,\\d+)*(\\.\\d+)?$'

// Helper function to safely get translations
function getTranslation(key: string): string {
  try {
    const t = i18n.global.t as TranslationFunction
    return t(key)
  } catch (error) {
    console.warn(`Translation not found for key: ${key}`)
    return key
  }
}

// Helper function to parse currency value (replacement for parseCurrency from vue-currency-input)
function parseCurrencyValue(value: string | number, locale?: string): number | undefined {
  if (!value && value !== 0) return undefined
  
  try {
    const stringValue = value.toString()
    const currentLocale = locale || (i18n.global.locale as any).value || 'he'
    
    // Remove thousands separators and handle decimal separators based on locale
    let cleanValue: string
    if (currentLocale === 'he') {
      // Hebrew locale typically uses comma as thousands separator and period as decimal
      cleanValue = stringValue.replace(/,/g, '')
    } else {
      // Default to US locale handling
      cleanValue = stringValue.replace(/,/g, '')
    }
    
    const numericValue = parseFloat(cleanValue)
    return isNaN(numericValue) ? undefined : numericValue
  } catch (error) {
    console.warn('Failed to parse currency value:', value)
    return undefined
  }
}

function normalizeRevenueIndication(result: RawRevenueIndication): RevenueIndication {
  const normalizedData = Array.isArray(result.data)
    ? result.data.map((shop) => ({
        shopId: Number(shop.shopId),
        sectionIds: Array.isArray(shop.sectionIds)
          ? shop.sectionIds.map((sectionId) => Number(sectionId))
          : [],
      }))
    : []

  return {
    data: normalizedData,
    length: typeof result.length === 'number' ? result.length : normalizedData.length,
  }
}

function normalizeHeaderValue(header: unknown): string {
  return String(header ?? '')
    .trim()
    .toLowerCase()
}

function getLocalizedRevenueHeaderValue(
  key: RevenueFileHeaderKey,
  locale: SupportedLocale
): string {
  const translations = localeMessages[locale]

  if (key === 'ChainShopId') {
    return `${translations.ChainShopId} - ${translations.NotMandatory}`
  }

  const definition = revenueFileHeaderDefinitions.find((headerDefinition) => headerDefinition.key === key)
  if (!definition) {
    return key
  }

  return String(translations[definition.translationKey])
}

const revenueService = {
  async getRevenueIndication(
    month: string,
    year: string,
    options: RevenueIndicationOptions = {}
  ): Promise<RevenueIndicationResult> {
    const path = '/revenues/GetPostedShops'
    const store = useAppStore()
    const applyToStore = options.applyToStore ?? true
    
    try {
      const result = await request.get<RawRevenueIndication>(
        import.meta.env.VITE_REVENUE_API_URL + path,
        { month, year }
      )
      
      if (!result) {
        return {
          success: false,
          error: 'request_failed',
          details: `Revenue indication request returned null for ${month}/${year}`
        }
      }

      const normalizedResult = normalizeRevenueIndication(result)

      if (applyToStore) {
        store.setRevenueIndication(normalizedResult)
      }
      return { success: true, data: normalizedResult }
    } catch (error) {
      console.error('Failed to get revenue indication:', error)
      return {
        success: false,
        error: 'unexpected_error',
        details: error instanceof Error ? error.message : String(error)
      }
    }
  },

  async postRevenue(
    body: unknown
  ): Promise<{ status: number; data: PostRevenueResponse[] | null }> {
    const path = '/revenues/postrevenue'
    return await request.postWithStatus<PostRevenueResponse[]>(
      import.meta.env.VITE_REVENUE_API_URL + path,
      body
    )
  },

  postRevenueDone(postRevenueResponse: PostRevenueResponse[] = []): void {
    const store = useAppStore()
    const allowedShops = store.getAllowedShopsList

    if (!allowedShops || !Array.isArray(allowedShops)) {
      console.warn('No allowed shops list found in store')
      return
    }

    allowedShops.forEach((element: Shop) => {
      element.sections?.forEach((section: Section) => {
        if (
          postRevenueResponse &&
          postRevenueResponse.some(
            (shop) =>
              shop.updateSucceed === true &&
              shop.shopId === element.shopId &&
              shop.sectionId === section.sectionId
          )
        ) {
          section.revenueExists = true
        }
        section.revenueValue = 0
      })
    })
    
    store.setAllowedShopsList(allowedShops)
  },

  prepareDataForFileSample(revenueList: Shop[], selectedDate: Date): RevenueItem[] {
    return revenueList
      .map((revenueInputItem) =>
        (revenueInputItem.sections || []).map((shopSection: Section): RevenueItem => ({
          ShoppingCenterName: revenueInputItem.shoppingCenterName || '',
          ShopName: revenueInputItem.shopName,
          ChainShopId: revenueInputItem.chainShopId || null,
          SectionId: shopSection.sectionId,
          SectionName: shopSection.sectionName || '',
          Month: selectedDate.getMonth() + 1,
          Year: selectedDate.getFullYear(),
          RevenueInteger:
            shopSection.revenueValue !== undefined && shopSection.revenueValue !== null
              ? shopSection.revenueValue
              : null,
          IsShopDisabled: revenueInputItem.isDisabled || false,
        }))
      )
      .reduce((revenueArr, sectionRevenueItem) => revenueArr.concat(sectionRevenueItem), [])
  },

  prepareRevenueData(revenueList: Shop[]): PreparedRevenueData[] {
    return revenueList
      .map((revenueInputItem) =>
        (revenueInputItem.sections || []).map((shopSection: Section): PreparedRevenueData => ({
          revenueValue: shopSection.revenueValue !== undefined && shopSection.revenueValue !== null
            ? parseCurrencyValue(shopSection.revenueValue, (i18n.global.locale as any).value)
            : undefined,
          shopId: revenueInputItem.shopId,
          sectionId: shopSection.sectionId,
          shoppingCenterGroupId: revenueInputItem.shoppingCenterGroupId || 0,
          chainShopId: revenueInputItem.chainShopId || null,
        }))
      )
      .reduce((revenueArr, sectionRevenueItem) => revenueArr.concat(sectionRevenueItem), [])
      .filter((revenueInputItem) =>
        new RegExp(numberWithSeparatorsRegEx).test(revenueInputItem.revenueValue?.toString() || '')
      )
  },

  validateRevenueData(
    revenuesExternalFormat: RevenueItem[],
    revenuesInternalFormat: Shop[]
  ): ValidationResult {
    const validationResult: ValidationResult = {
      success: false,
      errors: [],
    }

    if (revenuesExternalFormat.length === 0) {
      validationResult.errors.push(this.createError('ErrorEmptyFile'))
    }

    const firstItem = revenuesExternalFormat[0]
    if (firstItem) {
      const mixedMonths = revenuesExternalFormat.find(
        (item) => item.Month !== firstItem.Month || item.Year !== firstItem.Year
      )
      
      if (mixedMonths) {
        validationResult.errors.push(
          this.createError(
            'ErrorMixedDates',
            `${mixedMonths.Month}-${mixedMonths.Year} | ${firstItem.Month}-${firstItem.Year}`,
            `${mixedMonths.ShopName},${firstItem.ShopName}`
          )
        )
      }
    }

    const outOfRangeMonth = revenuesExternalFormat.find((item) => item.Month > 12 || item.Month < 1)
    if (outOfRangeMonth) {
      validationResult.errors.push(
        this.createError(
          'ErrorMonthOutOfRange',
          outOfRangeMonth.Month.toString(),
          `${outOfRangeMonth.ShoppingCenterName} ${outOfRangeMonth.ShopName}`
        )
      )
    }

    const outOfRangeYear = revenuesExternalFormat.find((item) => item.Year < 2019 || item.Year >= 2100)
    if (outOfRangeYear) {
      validationResult.errors.push(
        this.createError(
          'ErrorYearOutOfRange',
          outOfRangeYear.Year.toString(),
          `${outOfRangeYear.ShoppingCenterName} ${outOfRangeYear.ShopName}`
        )
      )
    }

    if (revenuesExternalFormat.some((revenueItem) => !('RevenueInteger' in revenueItem))) {
      validationResult.errors.push(this.createError('ErrorNoRevenue'))
    }
    if (revenuesExternalFormat.some((revenueItem) => !('Year' in revenueItem))) {
      validationResult.errors.push(this.createError('ErrorNoYear'))
    }
    if (revenuesExternalFormat.some((revenueItem) => !('Month' in revenueItem))) {
      validationResult.errors.push(this.createError('ErrorNoMonth'))
    }
    if (revenuesExternalFormat.some((revenueItem) => !('ShopName' in revenueItem))) {
      validationResult.errors.push(this.createError('ErrorNoShopName'))
    }

    revenuesExternalFormat.forEach((revenueItem) => {
      const revenueItemIdentifier = [
        revenueItem.ShoppingCenterName,
        revenueItem.ShopName,
        revenueItem.SectionName,
      ]
        .filter((value) => value)
        .join(' ')

      // Convert store Shop to shopsService Shop format for compatibility
      const serviceShops = revenuesInternalFormat.map(shop => ({
        shopId: shop.shopId,
        shopName: shop.shopName,
        chainId: shop.chainId ?? 0,
        chainName: shop.chainName,
        shoppingCenterId: shop.shoppingCenterId ?? 0,
        shoppingCenterName: shop.shoppingCenterName ?? '',
        shoppingCenterGroupName: shop.shoppingCenterGroupName ?? '',
        shoppingCenterGroupId: shop.shoppingCenterGroupId ?? 0,
        sections: (shop.sections || []).map(section => ({
          sectionId: section.sectionId,
          sectionName: section.sectionName ?? '',
          sectionExternalId: section.sectionExternalId,
          shoppingCenterGroupId: section.shoppingCenterGroupId,
          shoppingCenterGroupName: section.shoppingCenterGroupName,
          updateSucceed: section.updateSucceed,
          relatedFailureParameter: section.relatedFailureParameter,
          failureReason: section.failureReason,
          revenueValue: section.revenueValue,
        })),
        creationDate: shop.creationDate,
        deactivationDate: shop.deactivationDate,
        chainShopId: shop.chainShopId ?? null,
        shoppingCenterShopId: shop.shoppingCenterShopId ?? '',
        groupsRelated: shop.groupsRelated,
        membersRelated: shop.membersRelated,
        canEditShop: shop.canEditShop ?? false,
        isDisabled: shop.isDisabled ?? false,
      }))

      const ShopFromSystem = shopsService.getShopFromCollection(serviceShops, {
        ShopName: revenueItem.ShopName,
        ShoppingCenterName: revenueItem.ShoppingCenterName,
        ChainShopId: revenueItem.ChainShopId ?? undefined,
        SectionId: revenueItem.SectionId.toString(),
      })

      if (ShopFromSystem == null) {
        validationResult.errors.push(
          this.createError(
            'ErrorShopNotFound',
            revenueItemIdentifier
          )
        )
      }

      if (revenueItem.RevenueInteger === null || revenueItem.RevenueInteger === undefined) {
        validationResult.errors.push(
          this.createError(
            'ErrorNullRevenueIntegerValue',
            revenueItemIdentifier
          )
        )
      }

      if (revenueItem.RevenueInteger !== null && revenueItem.RevenueInteger < 0) {
        validationResult.errors.push(
          this.createError(
            'ErrorNegativeRevenueIntegerValue',
            revenueItemIdentifier
          )
        )
      }
    })

    if (validationResult.errors.length === 0) {
      validationResult.success = true
    }
    return validationResult
  },

  revenueExistsForShop(shopId: number, sectionId: number): boolean {
    const store = useAppStore()
    const shops = store.getRevenueIndication
    
    if (!shops || !shops.data || shops.data.length === 0) {
      return false
    }
    
    const shop = shops.data.find(
      (shop) =>
        Number(shop.shopId) === shopId &&
        shop.sectionIds.some((section) => Number(section) === sectionId)
    )
    return !!shop
  },

  createError(
    errorTranslationKey: string,
    relatedParameterValues?: string,
    identifierDescription?: string
  ): ValidationError {
    return {
      failureReason: errorTranslationKey,
      relatedFailureParameter: relatedParameterValues,
      identifierDescription,
    }
  },

  getRevenueFileHeaderTranslation(): HeaderTranslation[] {
    return revenueFileHeaderDefinitions.map((headerDefinition) => ({
      key: headerDefinition.key,
      header:
        headerDefinition.key === 'ChainShopId'
          ? `${getTranslation('ChainShopId')} - ${getTranslation('NotMandatory')}`
          : getTranslation(headerDefinition.translationKey),
    }))
  },

  getRevenueFileHeaderAliases(): Record<string, RevenueFileHeaderKey> {
    const supportedLocales = Object.keys(localeMessages) as SupportedLocale[]

    return revenueFileHeaderDefinitions.reduce<Record<string, RevenueFileHeaderKey>>(
      (aliases, headerDefinition) => {
        aliases[normalizeHeaderValue(headerDefinition.key)] = headerDefinition.key

        supportedLocales.forEach((locale) => {
          aliases[normalizeHeaderValue(getLocalizedRevenueHeaderValue(headerDefinition.key, locale))] =
            headerDefinition.key

          if (headerDefinition.key === 'ChainShopId') {
            aliases[normalizeHeaderValue(localeMessages[locale].ChainShopId)] = headerDefinition.key
          }
        })

        return aliases
      },
      {}
    )
  },

  isValueString(sheet: XLSX.WorkSheet, colNum: number, startingRow: number, endingRow: number): boolean {
    const refData: any[] = []
    for (let row = startingRow + 1; row <= endingRow; ++row) {
      const ref = XLSX.utils.encode_cell({ r: row, c: colNum })
      if (sheet[ref]) {
        refData.push(sheet[ref].v)
      }
    }
    const stringValues = refData.map((value) => value?.toString())
    const existingValues = stringValues.filter((value) => value)
    return existingValues.some((value) => !textHelpers.numericValidator(value))
  },

  setColumnFormatByContent(column: string, sheet: XLSX.WorkSheet): void {
    const stringFmt = '@'
    const totalColumns = sheet['!ref']
    if (!totalColumns) return

    const colNum = XLSX.utils.decode_col(column)
    const range = XLSX.utils.decode_range(totalColumns)
    const startingRow = range.s.r
    const endingRow = range.e.r

    if (this.isValueString(sheet, colNum, startingRow, endingRow)) {
      for (let row = startingRow + 1; row <= endingRow; ++row) {
        const ref = XLSX.utils.encode_cell({ r: row, c: colNum })
        if (!sheet[ref]) continue
        if (sheet[ref].v === null) {
          sheet[ref].v = ''
          sheet[ref].t = 's'
        }
        sheet[ref].z = stringFmt
      }
    } else {
      for (let row = startingRow + 1; row <= endingRow; ++row) {
        const ref = XLSX.utils.encode_cell({ r: row, c: colNum })
        if (!sheet[ref]) continue
        sheet[ref].t = 'n'
      }
    }
  },

  translateColumnValue(column: string, sheet: XLSX.WorkSheet): void {
    const totalColumns = sheet['!ref']
    if (!totalColumns) return

    const colNum = XLSX.utils.decode_col(column)
    const range = XLSX.utils.decode_range(totalColumns)
    const startingRow = range.s.r
    const endingRow = range.e.r
    
    for (let row = startingRow + 1; row <= endingRow; ++row) {
      const ref = XLSX.utils.encode_cell({ r: row, c: colNum })
      if (!sheet[ref]) continue
      sheet[ref].t = 's'
      sheet[ref].v = sheet[ref].v === false ? getTranslation('No') : getTranslation('Yes')
    }
  },

  getIndexedHeaders(sheet: XLSX.WorkSheet): IndexedHeader[] {
    const headers: any[] = []
    const totalColumns = sheet['!ref']
    if (!totalColumns) return []

    const range = XLSX.utils.decode_range(totalColumns)
    let column
    const row = range.s.r
    const rangeEndColumnLetter = range.e.c
    const rangeStartColumnLetter = range.s.c
    
    for (column = rangeStartColumnLetter; column <= rangeEndColumnLetter; ++column) {
      const cellref = sheet[XLSX.utils.encode_cell({ c: column, r: row })]
      headers.push(cellref)
    }
    
    const alphabet = headers.map((_q, w) => String.fromCharCode(w + 97).toUpperCase())
    const indexedHeaders = headers.map((element, index) => ({
      ...element,
      adress: alphabet[index],
    }))
    
    return indexedHeaders
  },

  getColumnAddressByName(columnName: string, sheet: XLSX.WorkSheet): string | undefined {
    const translationName = this.getRevenueFileHeaderTranslation().find(
      (translation) => translation.key === columnName
    )
    if (!translationName) return undefined

    const matchingHeader = this.getIndexedHeaders(sheet).find(
      (header) => header.v === translationName.header
    )
    return matchingHeader?.adress
  },

  generateFile(fileName: string, content: RevenueItem[]): void {
    if (content) {
      const translatedHeaders = this.getRevenueFileHeaderTranslation()
      const translatedContent = content.map((item) => {
        const newItem: any = { ...item }
        for (const [key] of Object.entries(item)) {
          const match = translatedHeaders.find((header) => header.key === key)
          if (match && match.header !== key) {
            delete Object.assign(newItem, { [match.header]: newItem[key] })[key]
          }
        }
        return newItem
      })

      const workBook = XLSX.utils.book_new()
      const dataSheet = XLSX.utils.json_to_sheet(translatedContent)
      const chainShopIdCell = this.getColumnAddressByName('ChainShopId', dataSheet)
      const isShopDisabledCell = this.getColumnAddressByName('IsShopDisabled', dataSheet)
      
      if (chainShopIdCell) {
        this.setColumnFormatByContent(chainShopIdCell, dataSheet)
      }
      if (isShopDisabledCell) {
        this.translateColumnValue(isShopDisabledCell, dataSheet)
      }
      
      XLSX.utils.book_append_sheet(workBook, dataSheet, 'sheet')
      XLSX.writeFile(workBook, fileName)
    }
  },

  convertCellsToRows(sheet: XLSX.WorkSheet): any[][] {
    const rows: any[][] = []
    const range = XLSX.utils.decode_range(sheet['!ref'] || '')
    const startingRow = range.s.r
    const endingRow = range.e.r
    const rangeEndColumnLetter = range.e.c
    const rangeStartColumnLetter = range.s.c

    for (let rowNum = startingRow; rowNum <= endingRow; rowNum++) {
      const row: any[] = []
      for (let colNum = rangeStartColumnLetter; colNum <= rangeEndColumnLetter; colNum++) {
        const nextCell = sheet[XLSX.utils.encode_cell({ r: rowNum, c: colNum })]
        if (typeof nextCell === 'undefined') {
          row.push(undefined)
        } else {
          row.push(nextCell.w)
        }
      }
      rows.push(row)
    }
    return rows
  },

  adaptFileReaderForIE(): void {
    if ((FileReader.prototype as any).readAsBinaryString === undefined) {
      ;(FileReader.prototype as any).readAsBinaryString = function (fileData: File) {
        let binary = ''
        const pt = this
        const reader = new FileReader()
        reader.onload = function () {
          const bytes = new Uint8Array(reader.result as ArrayBuffer)
          const length = bytes.byteLength
          for (let i = 0; i < length; i++) {
            binary += String.fromCharCode(bytes[i])
          }
          ;(pt as any).content = binary
          pt.onload?.(new ProgressEvent('load'))
        }
        reader.readAsArrayBuffer(fileData)
      }
    }
  },

  readFileAsString(file: File): Promise<string> {
    this.adaptFileReaderForIE()
    const reader = new FileReader()
    
    return new Promise<string>((resolve, reject) => {
      reader.onerror = () => {
        reader.abort()
        reject(new DOMException('Problem parsing input file.'))
      }
      
      reader.onload = function (e) {
        let data: string
        if (!e) {
          data = (reader as any).content
        } else {
          data = e.target?.result as string
        }
        resolve(data)
      }
      
      ;(reader as any).readAsBinaryString(file)
    })
  },

  processWorksheetData(data: string): unknown[][] {
    const isEmptyArr = (arr: unknown[]) => arr.filter((x) => undefined !== x).length === 0
    const workBook = XLSX.read(data, { type: 'binary' })
    const sheet = workBook.Sheets[Object.keys(workBook.Sheets)[0]]
    
    if (!sheet) {
      throw new Error('No valid sheet found in workbook')
    }
    
    return this.convertCellsToRows(sheet).filter((row) => !isEmptyArr(row))
  },

  mapRowsToObjects(rows: unknown[][]): Record<string, unknown>[] {
    const headerAliases = this.getRevenueFileHeaderAliases()
    const headers = rows.shift()?.map((cell) => ({
      heading: cell,
    })) || []

    headers.forEach((heading) => {
      const normalizedHeader = normalizeHeaderValue(heading.heading)
      heading.heading = headerAliases[normalizedHeader] || heading.heading
    })

    return rows
      .map((row) => {
        return headers.map((_header, index) => {
          const cell = row[index]
          const headerKey = String(headers[index].heading || `column_${index}`)
          return {
            [headerKey]: cell ?? null,
          }
        })
      })
      .map((row) =>
        row.reduce(
          (obj, item) =>
            Object.assign(obj, {
              [Object.keys(item)[0]]: Object.values(item)[0],
            }),
          {}
        )
      )
  },

  validateAndTransformRowData(mappedRows: Record<string, unknown>[]): RevenueItem[] {
    const validatedRows: RevenueItem[] = mappedRows
      .map((row: Record<string, unknown>) => {
        // Transform revenue value if present
        if (row.RevenueInteger) {
          row.RevenueInteger = this.transformRevenueValueToNumber(row.RevenueInteger)
        }
        
        // Transform section ID if present
        if (row.SectionId) {
          row.SectionId = this.transformSectionToNumber(row.SectionId)
        }
        
        // Create validated revenue item
        const revenueItem: Partial<RevenueItem> = {
          ShoppingCenterName: String(row.ShoppingCenterName || ''),
          ShopName: String(row.ShopName || ''),
          ChainShopId: row.ChainShopId ? String(row.ChainShopId) : null,
          SectionId: Number(row.SectionId) || 0,
          SectionName: String(row.SectionName || ''),
          Month: Number(row.Month) || 0,
          Year: Number(row.Year) || 0,
          RevenueInteger: row.RevenueInteger as number | null,
          IsShopDisabled: Boolean(row.IsShopDisabled),
        }
        
        return revenueItem as RevenueItem
      })
      .filter((item): item is RevenueItem => {
        // Filter out invalid items
        return Boolean(
          item.ShopName &&
          item.SectionId &&
          item.Month > 0 &&
          item.Year > 0
        )
      })

    return validatedRows
  },

  async transformFileToObject(file: File): Promise<RevenueItem[]> {
    try {
      const data = await this.readFileAsString(file)
      const rows = this.processWorksheetData(data)
      const mappedRows = this.mapRowsToObjects(rows)
      return this.validateAndTransformRowData(mappedRows)
    } catch (error) {
      console.error('Failed to transform file to objects:', error)
      throw new Error('Failed to process Excel file')
    }
  },

  transformRevenueValueToNumber(value: unknown): number | undefined {
    if (value || value === 0) {
      const revenue = parseCurrencyValue(value?.toString() || '', (i18n.global.locale as any).value)
      return revenue
    }
    return undefined
  },

  transformSectionToNumber(value: unknown): number | undefined {
    if (value) {
      return parseInt(String(value))
    }
    return undefined
  },
}

export default revenueService
